// src/store/index.js - État global avec Zustand
import { create } from 'zustand';
import { authAPI, paymentAPI, messageAPI } from '../services/api';
import E2E from '../services/e2eService';
import { socketService } from '../services/socket';
import toast from 'react-hot-toast';

// ============================================
// STORE AUTH
// ============================================
export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  initialized: false,
  isLoading: false,

  initialize: async () => {
    // Chercher la session dans localStorage (remember me) ou sessionStorage (session courante)
    const fromLocal = !!localStorage.getItem('refresh_token');
    const store = fromLocal ? localStorage : sessionStorage;
    const token = store.getItem('access_token');
    const refreshToken = store.getItem('refresh_token');
    const user = store.getItem('user');

    if (token && user) {
      // Token présent — connecter directement
      set({ user: JSON.parse(user), isAuthenticated: true, initialized: true });
      socketService.connect(token);
    } else if (!token && !refreshToken) {
      // Aucun token — afficher le login
      set({ initialized: true, isAuthenticated: false });
    } else if (token && !user) {
      // État incohérent — nettoyer
      ['access_token','refresh_token','user'].forEach(k => {
        localStorage.removeItem(k); sessionStorage.removeItem(k);
      });
      set({ initialized: true, isAuthenticated: false });
    } else if (refreshToken && user) {
      // Pas d'access_token mais refresh token disponible — reconnecter automatiquement
      try {
        const res = await authAPI.refresh(refreshToken);
        const { access_token, refresh_token: new_refresh } = res.data || res;
        store.setItem('access_token', access_token);
        if (new_refresh) store.setItem('refresh_token', new_refresh);
        const parsedUser = JSON.parse(user);
        set({ user: parsedUser, isAuthenticated: true, initialized: true });
        socketService.connect(access_token);
        console.log('✅ Reconnexion automatique réussie');
      } catch(e) {
        ['access_token','refresh_token','user'].forEach(k => {
          localStorage.removeItem(k); sessionStorage.removeItem(k);
        });
        set({ initialized: true, isAuthenticated: false });
      }
    } else {
      set({ initialized: true, isAuthenticated: false });
    }
  },

  login: async (credentials, rememberMe = true) => {
    set({ isLoading: true });
    try {
      const res = await authAPI.login(credentials);
      if (res.requires2FA || res.data?.requires2FA) {
        return { requires2FA: true, tempToken: res.tempToken || res.data?.tempToken };
      }
      const { access_token, refresh_token, user } = res.data || res;
      // Stocker selon "se souvenir de moi" : localStorage (persistant) ou sessionStorage (onglet)
      const store = rememberMe ? localStorage : sessionStorage;
      if (!rememberMe) ['access_token','refresh_token','user'].forEach(k => localStorage.removeItem(k));
      store.setItem('access_token', access_token);
      store.setItem('refresh_token', refresh_token);
      store.setItem('user', JSON.stringify(user));
      set({ user, isAuthenticated: true });
      socketService.connect(access_token);
      // Initialiser E2E
      try {
        const { publicKey } = await E2E.init(user.id);
        const token = access_token;
        fetch('/api/profile/public-key', {
          method: 'PUT',
          headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ public_key: publicKey })
        });
      } catch(e) { console.warn('E2E init failed:', e); }
      toast.success(`Bienvenue ${user.display_name} ! 👋`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.error };
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    // Nettoyer immédiatement sans attendre l'API
    ['access_token','refresh_token','user'].forEach(k => {
      localStorage.removeItem(k); sessionStorage.removeItem(k);
    });
    socketService.disconnect();
    set({ user: null, isAuthenticated: false });
    window.location.replace('/login');
    // Appel API en arrière-plan
    try { authAPI.logout(); } catch {}
  },
}));

// ============================================
// STORE CONVERSATIONS & MESSAGES
// ============================================
export const useMessageStore = create((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: {}, // { conversationId: [msg, ...] }
  typingUsers: {}, // { conversationId: { userId: name } }
  onlineUsers: {}, // { userId: true/false }
  isLoading: false,

  fetchConversations: async () => {
    try {
      const res = await messageAPI.getConversations();
      set({ conversations: res.data || [] });
    } catch {}
  },

  setActiveConversation: async (conversationId) => {
    // Rejoindre la room socket de la conversation
    if (conversationId) socketService.joinConversation(conversationId);
    set({ activeConversationId: conversationId });
    const state = get();
    if (!state.messages[conversationId]) {
      await state.fetchMessages(conversationId);
    }
    // Marquer tous les messages comme lus
    const msgs = get().messages[conversationId] || [];
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg) socketService.markRead(conversationId, lastMsg.id);
    // Reset badge unread immédiatement à l'ouverture
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      )
    }));
  },

  fetchMessages: async (conversationId) => {
    set({ isLoading: true });
    try {
      const res = await messageAPI.getMessages(conversationId);
      set(state => ({
        messages: { ...state.messages, [conversationId]: res.data }
      }));
    } catch {} finally {
      set({ isLoading: false });
    }
  },

  updateMessage: (conversationId, messageId, updates) => {
    set(state => ({
      messages: {
        ...state.messages,
        [conversationId]: (state.messages[conversationId] || []).map(m =>
          m.id === messageId ? { ...m, ...updates } : m
        ),
      }
    }));
  },

  addMessage: (message) => {
    // Normaliser le content en string JSON si c'est un objet
    if (message.content && typeof message.content === 'object') {
      message = { ...message, content: JSON.stringify(message.content) };
    }
    // Assurer is_read par défaut
    if (message.is_read === undefined) message = { ...message, is_read: false };
    const convId = message.conversation_id;
    // Si la conversation est active, marquer comme lu
    if (get().activeConversationId === convId && message.sender_id !== useAuthStore.getState().user?.id) {
      socketService.markRead(convId, message.id);
    }
    set(state => {
      const existing = state.messages[convId] || [];
      if (existing.some(m => m.id === message.id)) return state;
      // Remplacer le message temp correspondant s'il existe, sinon juste ajouter
      const filtered = existing.filter(m =>
        !(m.isTemp && m.content === message.content && m.sender_id === message.sender_id)
      );
      return {
        messages: {
          ...state.messages,
          [convId]: [...filtered, message],
        },
        conversations: state.conversations.map(c => {
          if (c.id !== convId) return c;
          const isActive = state.activeConversationId === convId;
          const fromMe = message.sender_id === useAuthStore.getState().user?.id;
          const newUnread = (isActive || fromMe) ? 0 : (parseInt(c.unread_count || 0, 10)) + 1;
          return {
            ...c,
            last_message_content: message.content,
            last_message_at: message.created_at,
            updated_at: message.created_at,
            unread_count: newUnread,
          };
        }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
      };
    });
  },

  sendMessage: async (conversationId, content, contactId, type, expiresAt) => {
    // Optimistic update (afficher immédiatement)
    const user = useAuthStore.getState().user;
    const tempMsg = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: user.display_name,
      type: type || 'text',
      content,
      created_at: new Date().toISOString(),
      expires_at: expiresAt || null,
      isTemp: true,
      is_read: false,
    };
    get().addMessage(tempMsg);

    try {
      // Chiffrer le message E2E si possible
      let encryptedContent = content;
      let isEncrypted = false;
      if (contactId) {
        try {
          const token = localStorage.getItem('access_token');
          const pkRes = await fetch('/api/users/' + contactId + '/public-key', {
            headers: { Authorization: 'Bearer ' + token }
          });
          const pkData = await pkRes.json();
          if (pkData.success && pkData.public_key) {
            encryptedContent = await E2E.encrypt(content, pkData.public_key);
            isEncrypted = true;
          }
        } catch(e) { console.warn('E2E skip:', e.message); }
      }
      // Fallback REST shared logic
      const sendViaRest = async () => {
        const response = await messageAPI.sendMessage(conversationId, { content: encryptedContent, is_encrypted: isEncrypted, expires_at: expiresAt || undefined });
        if (response.success) {
          set(state => ({
            messages: {
              ...state.messages,
              [conversationId]: state.messages[conversationId]?.map(m =>
                m.id === tempMsg.id ? response.data : m
              ) || [],
            }
          }));
        }
      };

      // Essayer WebSocket d'abord, avec timeout de 5s puis fallback REST
      if (socketService.isConnected()) {
        let ackReceived = false;
        const fallbackTimer = setTimeout(async () => {
          if (!ackReceived) {
            ackReceived = true;
            try { await sendViaRest(); } catch(e) { console.error('Fallback REST erreur:', e); }
          }
        }, 5000);
        socketService.sendMessage(conversationId, encryptedContent, (response) => {
          if (ackReceived) return;
          ackReceived = true;
          clearTimeout(fallbackTimer);
          if (response.success) {
            set(state => ({
              messages: {
                ...state.messages,
                [conversationId]: state.messages[conversationId]?.map(m =>
                  m.id === tempMsg.id ? response.data : m
                ) || [],
              }
            }));
          } else {
            // Ack reçu mais erreur serveur — supprimer le message temp
            set(state => ({
              messages: {
                ...state.messages,
                [conversationId]: (state.messages[conversationId] || []).filter(m => m.id !== tempMsg.id),
              }
            }));
            toast.error(response.error || 'Erreur envoi message');
          }
        }, undefined, expiresAt);
      } else {
        // Pas de socket — REST direct
        await sendViaRest();
      }
    } catch(err) {
      console.error('Erreur envoi message:', err);
    }
  },

  setTyping: (conversationId, userId, userName, isTyping) => {
    set(state => {
      const conv = { ...(state.typingUsers[conversationId] || {}) };
      if (isTyping) conv[userId] = userName;
      else delete conv[userId];
      return { typingUsers: { ...state.typingUsers, [conversationId]: conv } };
    });
  },

  setOnlineStatus: (userId, online) => {
    set(state => ({
      onlineUsers: { ...state.onlineUsers, [userId]: online }
    }));
  },

  markConversationRead: (conversationId) => {
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      )
    }));
  },

  createConversation: async (participantIds, type = 'direct', name = null) => {
    try {
      const res = await messageAPI.createConversation({
        type, participant_ids: participantIds, name,
      });
      await get().fetchConversations();
      return res.data;
    } catch (err) {
      toast.error('Impossible de créer la conversation');
      return null;
    }
  },
}));

// ============================================
// STORE WALLET & PAIEMENTS
// ============================================
export const usePaymentStore = create((set, get) => ({
  wallet: null,
  transactions: [],
  isLoading: false,

  fetchWallet: async () => {
    try {
      const res = await paymentAPI.getWallet();
      set({ wallet: res.data });
    } catch {}
  },

  fetchTransactions: async (params = {}) => {
    set({ isLoading: true });
    try {
      const res = await paymentAPI.getTransactions(params);
      set({ transactions: res.data.transactions });
    } catch {} finally {
      set({ isLoading: false });
    }
  },

  sendPayment: async ({ receiverId, amount, note, conversationId, pin }) => {
    set({ isLoading: true });
    try {
      const res = await paymentAPI.sendPayment({
        receiver_id: receiverId,
        amount: parseFloat(amount),
        note,
        conversation_id: conversationId,
        pin,
      });
      // Rafraîchir le solde
      await get().fetchWallet();
      toast.success(`💸 ${amount}FCFA  envoyé avec succès !`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.error };
    } finally {
      set({ isLoading: false });
    }
  },

  requestPayment: async ({ fromUserId, amount, note, conversationId }) => {
    try {
      const res = await paymentAPI.requestPayment({
        from_user_id: fromUserId,
        amount: parseFloat(amount),
        note,
        conversation_id: conversationId,
      });
      toast.success(`📥 Demande de ${amount}FCFA  envoyée`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.error };
    }
  },

  splitBill: async ({ conversationId, totalAmount, title, participantIds }) => {
    try {
      const res = await paymentAPI.splitBill({
        conversation_id: conversationId,
        total_amount: parseFloat(totalAmount),
        title,
        participant_ids: participantIds,
      });
      toast.success(`⚡ Facture partagée entre ${participantIds.length + 1} personnes`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.error };
    }
  },
}));
